import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, Frame } from 'lucide-react';
import CustomCursor from '@/components/CustomCursor';
import CodeRain from '@/components/CodeRain';
import Header from '@/components/Header';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ArtistCard({ artist }) {
  const inner = (
    <div className="group block">
      <div className="relative aspect-square overflow-hidden border border-[#111] bg-[#0a0a0a] transition-shadow duration-500 group-hover:shadow-[0_18px_40px_-16px_rgba(144,224,239,0.16)]">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-30 group-hover:opacity-60 transition-opacity duration-500"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0) 78%)',
          }}
        />
        {artist.pfp_url ? (
          <img
            src={artist.pfp_url}
            alt={artist.name}
            loading="lazy"
            className="relative w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.02] group-hover:brightness-110"
          />
        ) : (
          <div className="relative w-full h-full flex items-center justify-center">
            <Frame className="w-6 h-6 text-[#222]" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/25 group-hover:bg-black/0 transition-colors duration-500" />
      </div>
      <div className="mt-3">
        <h3 className="text-[14px] font-outfit font-semibold text-white uppercase tracking-tight leading-tight">
          {artist.name}
        </h3>
        <div className="mt-1 flex items-center gap-2 text-[10px] font-mono text-[#8a8a8a] uppercase tracking-[0.16em]">
          {artist.medium && <span className="truncate">{artist.medium}</span>}
          {artist.medium && artist.location && <span>·</span>}
          {artist.location && <span className="truncate">{artist.location}</span>}
        </div>
        {typeof artist.artwork_count === 'number' && artist.artwork_count > 0 && (
          <div className="mt-1 text-[10px] font-mono text-[#666]">
            {artist.artwork_count} {artist.artwork_count === 1 ? 'work' : 'works'}
          </div>
        )}
      </div>
    </div>
  );

  if (!artist.slug) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        data-testid={`artist-card-nolink-${artist.id}`}
      >
        {inner}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      whileHover={{ y: -3, scale: 1.012, transition: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] } }}
      data-testid={`artist-card-${artist.slug}`}
    >
      <Link to={`/artists/${artist.slug}`} className="block">
        {inner}
      </Link>
    </motion.div>
  );
}

export default function PublicArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/artists`)
      .then((r) => setArtists(r.data.artists || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen text-[#F5F5F5]">
      <CustomCursor />
      <CodeRain />
      <Header />

      <main className="relative z-10" style={{ paddingTop: 'calc(var(--header-height) + 32px)' }}>
        {/* Hero heading */}
        <section className="px-6 md:px-10 lg:px-16 pb-6 md:pb-10 border-b border-[#111]">
          <div className="max-w-7xl mx-auto">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#666]">
              THE RELATIVE GALLERY
            </p>
            <h1 className="mt-3 text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-outfit font-bold text-white tracking-tighter leading-[0.95] uppercase">
              Artists
            </h1>
            <p className="mt-5 max-w-xl text-[13px] font-mono text-[#888] leading-relaxed">
              Every artist we exhibit — investigating what it means to be human at a moment
              when machines begin to imagine.
            </p>
          </div>
        </section>

        <section className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-6 h-6 text-[#90E0EF] animate-spin" />
              </div>
            ) : artists.length === 0 ? (
              <div className="text-center py-24 border border-[#111]">
                <Frame className="w-10 h-10 text-[#222] mx-auto" />
                <p className="text-[13px] font-mono text-[#666] mt-4">
                  No artists published yet.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="public-artists-grid">
                {artists.map((a) => (
                  <ArtistCard key={a.id} artist={a} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
